import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";

const PERMISSIONS = [
    { key: "products", label: "Products & stock" },
    { key: "categories", label: "Categories" },
    { key: "orders", label: "Orders & refunds" },
    { key: "customers", label: "Customers" },
    { key: "discounts", label: "Discounts" },
    { key: "marketing", label: "Marketing & newsletter" },
    { key: "emails", label: "Email logs" },
    { key: "content", label: "Homepage content" },
    { key: "settings", label: "Delivery & settings" },
    { key: "audit", label: "Audit log" },
];

const EMPTY = { name: "", email: "", password: "", permissions: [] };

export default function StaffAdmin({ user }) {
    const [staff, setStaff] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const openEditor = (s) => {
        setEditing(s || null);
        setForm(s ? { name: s.name, email: s.email, password: "", permissions: s.permissions || [] } : EMPTY);
        setOpen(true);
    };

    const save = async () => {
        if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
        if (!editing && form.password.length < 8) return toast.error("Password must be at least 8 characters");
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/staff/${editing.id}`, { ...form, password: form.password || null });
                toast.success("Staff member updated");
            } else {
                await api.post("/admin/staff", form);
                toast.success("Staff account created");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const togglePerm = (key) =>
        setForm({
            ...form,
            permissions: form.permissions.includes(key)
                ? form.permissions.filter((p) => p !== key)
                : [...form.permissions, key],
        });

    return (
        <div data-testid="admin-staff">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-slate-500">Owner accounts have full access. Staff get only the permissions you tick.</p>
                <Button onClick={() => openEditor(null)} data-testid="admin-add-staff" className="rounded-full bg-[#0033A0] hover:bg-[#002277]">
                    <Plus className="h-4 w-4 mr-2" /> Add Staff
                </Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-staff-list">
                {staff.map((s) => (
                    <div key={s.id} className="flex items-center gap-4 p-4 flex-wrap" data-testid={`staff-row-${s.id}`}>
                        <div className="h-10 w-10 rounded-full bg-[#0033A0] text-white flex items-center justify-center font-heading font-bold shrink-0">
                            {(s.name || s.email)[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-40">
                            <p className="font-heading font-bold text-slate-900">{s.name}</p>
                            <p className="text-xs text-slate-500">{s.email}</p>
                        </div>
                        {s.role === "owner" ? (
                            <span className="rounded-full bg-[#40E0D0]/30 text-[#0033A0] text-xs font-bold px-3 py-1">Owner — full access</span>
                        ) : (
                            <>
                                <span className="text-xs text-slate-500">{(s.permissions || []).length} permissions</span>
                                <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditor(s)} data-testid={`edit-staff-${s.id}`}>
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                {s.id !== user.id && (
                                    <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                                        data-testid={`delete-staff-${s.id}`}
                                        onClick={() => window.confirm(`Remove ${s.name}'s access?`) && api.delete(`/admin/staff/${s.id}`).then(load).then(() => toast.success("Removed"))}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="staff-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="sf-name">Name *</Label>
                                <Input id="sf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sf-name" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="sf-email">Email *</Label>
                                <Input id="sf-email" type="email" value={form.email} disabled={!!editing}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sf-email" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="sf-password">{editing ? "New password (leave blank to keep)" : "Password (min 8 characters) *"}</Label>
                            <Input id="sf-password" type="password" value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sf-password" />
                        </div>
                        <div className="space-y-2">
                            <Label>Permissions</Label>
                            <div className="grid grid-cols-2 gap-2" data-testid="sf-permissions">
                                {PERMISSIONS.map((p) => (
                                    <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={form.permissions.includes(p.key)}
                                            onChange={() => togglePerm(p.key)} className="accent-[#0033A0]"
                                            data-testid={`sf-perm-${p.key}`} />
                                        {p.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="sf-save">
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
